import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(248, 250, 252, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(66, 153, 225, 0.2);
  padding: 1rem 3rem;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    z-index: 1001;
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.8rem;
  font-weight: 200;
  color: #1a365d;
  letter-spacing: 2px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  .highlight {
    color: #3182ce;
    text-shadow: 0 0 10px rgba(66, 153, 225, 0.5);
  }

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    gap: 0.5rem;
  }
`;

const LogoImage = styled.img`
  height: 35px;
  object-fit: contain;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    height: 30px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 3rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.5rem;
  align-items: center;
`;

const NavLink = styled(Link)<{ isActive: boolean }>`
  color: ${props => props.isActive ? '#3182ce' : '#4a5568'};
  text-decoration: none;
  font-weight: ${props => props.isActive ? 500 : 300};
  font-size: 1rem;
  letter-spacing: 1px;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: #3182ce;
    background: rgba(66, 153, 225, 0.05);
    transform: translateY(-1px);
  }

  ${props => props.isActive && `
    background: rgba(66, 153, 225, 0.1);
    &::after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 50%;
      transform: translateX(-50%);
      width: 4px;
      height: 4px;
      background: #3182ce;
      border-radius: 50%;
    }
  `}
`;

const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #718096;

  .pulse {
    width: 8px;
    height: 8px;
    background: #38a169;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

const LanguageSelector = styled.div`
  display: flex;
  background: rgba(66, 153, 225, 0.08);
  border-radius: 25px;
  padding: 0.25rem;
  border: 1px solid rgba(66, 153, 225, 0.2);
  backdrop-filter: blur(10px);
`;

const LanguageButton = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive ? 'linear-gradient(135deg, #4299e1, #63b3ed)' : 'transparent'};
  color: ${props => props.isActive ? '#ffffff' : '#4a5568'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 1px;
  min-width: 50px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.isActive ? 'linear-gradient(135deg, #4299e1, #63b3ed)' : 'rgba(66, 153, 225, 0.1)'};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #3182ce;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1002;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 250, 252, 0.98);
  backdrop-filter: blur(20px);
  padding: 6rem 2rem 2rem 2rem;
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 999;
  overflow-y: auto;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: slideInUp 0.3s ease-out;

  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MobileNavLink = styled(Link)<{ isActive: boolean }>`
  color: ${props => props.isActive ? '#3182ce' : '#4a5568'};
  text-decoration: none;
  font-weight: ${props => props.isActive ? 600 : 400};
  font-size: 1.1rem;
  letter-spacing: 1px;
  padding: 1rem;
  border-radius: 15px;
  transition: all 0.3s ease;
  background: ${props => props.isActive ? 'rgba(66, 153, 225, 0.1)' : 'transparent'};
  border: ${props => props.isActive ? '1px solid rgba(66, 153, 225, 0.2)' : '1px solid transparent'};

  &:hover {
    color: #3182ce;
    background: rgba(66, 153, 225, 0.05);
  }
`;



const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as 'en' | 'es';
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'es')) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Handle language change
  const handleLanguageChange = (language: 'en' | 'es') => {
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  // Handle body scroll lock for mobile menu
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [mobileMenuOpen]);

  const navigationItems = [
    { path: '/', label: t('nav.home') },
    { path: '/tracking', label: t('nav.tracking') },
    { path: '/history', label: t('nav.history') },
    { path: '/team', label: t('nav.team') },
    // { path: '/sponsors', label: t('nav.sponsors') },
    // { path: '/tourists', label: t('nav.tourists') }
  ];

  const isActiveRoute = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeMobileMenu();
    }
  };

  return (
    <>
      <HeaderContainer>
        <NavContainer>
          <Logo to="/" onClick={closeMobileMenu}>
            <LogoImage src="/images/logoamel.png" alt="Amel Yachts" />
            <span><span className="highlight">Arctic</span> Owl</span>
          </Logo>
          
          <Navigation>
            <NavLinks>
              {navigationItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  isActive={isActiveRoute(item.path)}
                >
                  {item.label}
                </NavLink>
              ))}
            </NavLinks>
            
            {/* <LiveIndicator>
              <div className="pulse"></div>
              {t('nav.tracking')}
            </LiveIndicator> */}
            
            <LanguageSelector>
              <LanguageButton
                isActive={i18n.language === 'en'}
                onClick={() => handleLanguageChange('en')}
              >
                EN
              </LanguageButton>
              <LanguageButton
                isActive={i18n.language === 'es'}
                onClick={() => handleLanguageChange('es')}
              >
                ES
              </LanguageButton>
            </LanguageSelector>
          </Navigation>

          <MobileMenuButton onClick={handleMobileMenuToggle}>
            {mobileMenuOpen ? '✕' : '☰'}
          </MobileMenuButton>
        </NavContainer>
      </HeaderContainer>

      <MobileMenu isOpen={mobileMenuOpen} onClick={handleBackgroundClick}>
        <MobileNavLinks>
          {navigationItems.map((item) => (
            <MobileNavLink
              key={item.path}
              to={item.path}
              isActive={isActiveRoute(item.path)}
              onClick={closeMobileMenu}
            >
              {item.label}
            </MobileNavLink>
          ))}
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '1rem',
            borderTop: '1px solid rgba(66, 153, 225, 0.2)',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <LiveIndicator>
              <div className="pulse"></div>
              {t('nav.tracking')}
            </LiveIndicator>
            
            <LanguageSelector>
              <LanguageButton
                isActive={i18n.language === 'en'}
                onClick={() => handleLanguageChange('en')}
              >
                EN
              </LanguageButton>
              <LanguageButton
                isActive={i18n.language === 'es'}
                onClick={() => handleLanguageChange('es')}
              >
                ES
              </LanguageButton>
            </LanguageSelector>
          </div>
        </MobileNavLinks>
      </MobileMenu>
    </>
  );
};

export default Header; 